import React, { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CreateFormQuestionPayload } from 'api/form';
import {
  FormGroupColor,
  FormQuestion,
  FormQuestionMetaValue,
  FormQuestionType,
} from 'api/form/types';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { Label, ProductiveHeading07, Text03 } from 'components/styles';
import { useCreateFieldQuestionMutation } from 'queries/form-template';
import { pascalCase } from 'utils/pascal-case';
import { DefaultFormQuestion, FormQuestionsList } from './constants';
import {
  CFQuestionMap,
  constructDefaultQuestionValues,
  useFormTemplateProjectFieldsList,
  useFormTemplateTaskFieldsList,
} from './utils';

const FormQuestionsWrapper = styled.div`
  padding: var(--layout-02) 0;
  height: 100%;
`;

const ListHeading = styled.p`
  ${ProductiveHeading07};
  margin-bottom: var(--spacing-02);
  padding: 0 var(--spacing-05);
`;
const ListHeadingHelpText = styled.div`
  font-size: var(--font-size-sm);
  ${Text03};
  padding: 0 var(--spacing-05);
`;

const GroupHeader = styled.div`
  ${Label};
  margin-top: var(--spacing-06);
  padding: 0 var(--spacing-05);
  margin-bottom: var(--spacing-03);
`;

const GroupItem = styled.div`
  display: flex;
  cursor: pointer;
  user-select: none;
  align-items: center;
  padding: var(--spacing-03) var(--spacing-03);
  margin: 0 var(--spacing-03);
  &:hover {
    background: var(--coolest-gray-20);
  }
`;
const GroupItemIcon = styled.div<{ backgroundColor: string }>`
  padding: var(--spacing-02);
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-right: var(--spacing-03);
  background: ${({ backgroundColor }) => backgroundColor};
`;
const FormQuestionsListWrapper = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100%;
  padding-bottom: var(--spacing-10);
  margin-top: var(--spacing-03);
`;
const CustomFieldWrapper = styled.div`
  margin-top: var(--layout-02);
`;
const CustomFieldList = styled.div`
  margin-top: var(--spacing-05);
`;

const UsedCustomFieldsWrapper = styled.div`
  pointer-events: none;
  opacity: 0.6;
`;
const ProjectCustomFieldHeading = styled.div`
  color: var(--gray-70);
  margin: var(--spacing-05) var(--spacing-05) var(--spacing-02);
`;
const QuestionItem = ({
  item,
  onClick,
}: {
  item: DefaultFormQuestion;
  onClick: (formQuestionType: FormQuestionType) => void;
}): JSX.Element => {
  const iconBackgroundColor = useMemo(() => {
    return FormGroupColor[pascalCase(item.group)];
  }, [item.group]);

  return (
    <GroupItem
      onClick={() => onClick(item.value)}
      data-cy={`form.question.selector-${item.label}`}
    >
      <GroupItemIcon backgroundColor={iconBackgroundColor}>
        {item.icon}
      </GroupItemIcon>
      <FormattedMessage
        id={`form_templates.question.group.item.${item.label}`}
      />
    </GroupItem>
  );
};

const CustomFieldQuestion = ({
  item,
  onClick,
}: {
  item: CFQuestionMap;
  onClick: (formQuestionType: FormQuestionType) => void;
}) => {
  const iconBackgroundColor = useMemo(() => {
    return FormGroupColor[pascalCase(item.formQuestionGroup)];
  }, [item.formQuestionGroup]);

  return (
    <GroupItem onClick={() => onClick(item.formQuestionType)}>
      <GroupItemIcon backgroundColor={iconBackgroundColor}>
        {item.formQuestionIcon}
      </GroupItemIcon>
      {item.fieldLabel}
    </GroupItem>
  );
};

export const FormQuestions: FC<{
  setCreateNewQuestionLoading: Dispatch<SetStateAction<boolean>>;
  templateId: number;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
}> = ({ setCreateNewQuestionLoading, templateId, setSelectedFormItem }) => {
  const groupedFormListQuestions = useMemo(() => {
    return FormQuestionsList.reduce((acc, currentValue) => {
      acc[currentValue.group] = [
        ...(acc[currentValue.group] || []),
        currentValue,
      ];

      return acc;
    }, {});
  }, []);

  const { mutate: createFieldQuestion } =
    useCreateFieldQuestionMutation(templateId);

  const CreateQuestion = ({
    formQuestionType,
    projectCustomField,
    taskCustomField,
  }: {
    formQuestionType: FormQuestionType;
    projectCustomField?: CFQuestionMap;
    taskCustomField?: CFQuestionMap;
  }) => {
    let payload = {} as CreateFormQuestionPayload;

    if (projectCustomField) {
      payload = {
        formQuestionType,
        projectFieldId: projectCustomField.fieldId,
        templateId,
        questionText: projectCustomField.fieldLabel || '',
        description: projectCustomField.description || '',
      };
      const isRatingField = projectCustomField.fieldType === FieldTypes.Rating;

      if (isRatingField) {
        payload = {
          ...payload,
          formQuestionType: FormQuestionType.StarRating,
          questionMeta: {
            numberOfStar: (projectCustomField.metaData as any).ratingScale,
            type: FormQuestionMetaValue.StarRating,
          },
        };
      }
    } else if (taskCustomField) {
      payload = {
        formQuestionType,
        taskFieldId: taskCustomField.fieldId,
        templateId,
        questionText: taskCustomField.fieldLabel || '',
        description: taskCustomField.description || '',
      };
    } else {
      payload = constructDefaultQuestionValues({
        type: formQuestionType,
        templateId,
      });
    }

    setCreateNewQuestionLoading(true);
    createFieldQuestion(payload, {
      onSuccess: (questionResponse) => {
        setCreateNewQuestionLoading(false);
        setSelectedFormItem(questionResponse);
      },
    });
  };

  const [availableProjectCustomFields, usedProjectCustomFields] =
    useFormTemplateProjectFieldsList(templateId);

  const [availableTaskCustomFields, usedTaskCustomFields] =
    useFormTemplateTaskFieldsList(templateId);

  return (
    <FormQuestionsWrapper>
      <ListHeading>
        <FormattedMessage id="form_templates.question" />
      </ListHeading>
      <ListHeadingHelpText>
        <FormattedMessage id="form_templates.question.help_text" />
      </ListHeadingHelpText>
      <FormQuestionsListWrapper>
        {Object.keys(groupedFormListQuestions).map((key) => {
          return (
            <div>
              <GroupHeader>
                <FormattedMessage id={`form_templates.question.group.${key}`} />
              </GroupHeader>
              <>
                {groupedFormListQuestions[key].map(
                  (item: DefaultFormQuestion) => {
                    return (
                      <QuestionItem
                        item={item}
                        onClick={(formQuestionType) => {
                          CreateQuestion({
                            formQuestionType,
                          });
                        }}
                      />
                    );
                  },
                )}
              </>
            </div>
          );
        })}

        <CustomFieldWrapper>
          <ListHeading>
            <FormattedMessage id="form_templates.project_field.heading" />
          </ListHeading>
          <ListHeadingHelpText>
            <FormattedMessage id="form_templates.project_field.desc" />
          </ListHeadingHelpText>
          <CustomFieldList>
            <ProjectCustomFieldHeading>
              <FormattedMessage id="available" />
            </ProjectCustomFieldHeading>
            {availableProjectCustomFields.length
              ? availableProjectCustomFields
                  .filter(
                    (field) =>
                      field.fieldType !== 'KEY_EVENTS' &&
                      field.fieldType !== 'INTERVALS',
                  )
                  .map((projectCustomField) => (
                    <CustomFieldQuestion
                      item={projectCustomField}
                      onClick={(formQuestionType) => {
                        CreateQuestion({
                          formQuestionType,
                          projectCustomField,
                        });
                      }}
                    />
                  ))
              : null}
            {usedProjectCustomFields.length ? (
              <>
                <ProjectCustomFieldHeading>
                  <FormattedMessage id="added" />
                </ProjectCustomFieldHeading>
                <UsedCustomFieldsWrapper>
                  {usedProjectCustomFields.map((projectCustomField) => (
                    <CustomFieldQuestion
                      item={projectCustomField}
                      onClick={(formQuestionType) => {
                        CreateQuestion({
                          formQuestionType,
                          projectCustomField,
                        });
                      }}
                    />
                  ))}
                </UsedCustomFieldsWrapper>
              </>
            ) : null}
          </CustomFieldList>
        </CustomFieldWrapper>

        <CustomFieldWrapper>
          <ListHeading>
            <FormattedMessage id="task_fields" />
          </ListHeading>
          <ListHeadingHelpText>
            <FormattedMessage id="form_templates.task_field_question" />
          </ListHeadingHelpText>
          <CustomFieldList>
            <ProjectCustomFieldHeading>
              <FormattedMessage id="available" />
            </ProjectCustomFieldHeading>
            {availableTaskCustomFields.length
              ? availableTaskCustomFields.map((taskCustomField) => (
                  <CustomFieldQuestion
                    item={taskCustomField}
                    onClick={(formQuestionType) => {
                      CreateQuestion({ formQuestionType, taskCustomField });
                    }}
                  />
                ))
              : null}
            {usedTaskCustomFields.length ? (
              <>
                <ProjectCustomFieldHeading>
                  <FormattedMessage id="added" />
                </ProjectCustomFieldHeading>
                <UsedCustomFieldsWrapper>
                  {usedTaskCustomFields.map((taskCustomField) => (
                    <CustomFieldQuestion
                      item={taskCustomField}
                      onClick={(formQuestionType) => {
                        CreateQuestion({ formQuestionType, taskCustomField });
                      }}
                    />
                  ))}
                </UsedCustomFieldsWrapper>
              </>
            ) : null}
          </CustomFieldList>
        </CustomFieldWrapper>
      </FormQuestionsListWrapper>
    </FormQuestionsWrapper>
  );
};
